import { MenuIconButton } from '@juvare/common-web-ui/MenuIconButton';
import { Slideout } from '@juvare/common-web-ui/Slideout';
import { SlideoutContent } from '@juvare/common-web-ui/SlideoutContent';
import { handleErrorSnackbar } from '@juvare/common-web-ui/SnackbarHelper';
import { Box, Link, List, ListItem, Tooltip, Typography } from '@mui/material';
import { parseISO } from 'date-fns';
import React from 'react';
import { useInboxClient, useInboxTranslation } from '../../Hooks';
import { NotificationItem, NotificationListItem } from '../../Models';
import { humanFileSize } from '../../Utils';
import { Response } from './Response';

export interface DetailsMenuItemProps {
    notification: NotificationItem;
}

export interface DetailsSectionsProps {
    notification: NotificationItem;
}

export interface DetailsOptions {
    menuItems?: (props: DetailsMenuItemProps) => React.ComponentProps<typeof MenuIconButton>['menuItems'];
    additionalDetails?: (props: DetailsSectionsProps) => React.ReactElement;
}

interface Props {
    reloadList: () => void;
    options?: DetailsOptions;
    notificationId?: string;
    setSelectedNotification: React.Dispatch<React.SetStateAction<NotificationListItem | undefined>>;
}

export const SlideoutInbox: React.FC<Props> = ({ options, reloadList, notificationId, setSelectedNotification }) => {
    const { t } = useInboxTranslation();
    const inboxClient = useInboxClient();
    const [notification, setNotification] = React.useState<NotificationItem>();
    const [submittingResponse, setSubmittingResponse] = React.useState(false);
    const [reloadFlag, setReloadFlag] = React.useState({});

    React.useEffect(() => {
        setNotification(undefined);
        if (notificationId) {
            inboxClient.getNotification(notificationId).then(setNotification).catch(handleErrorSnackbar);
        }
    }, [notificationId, inboxClient, reloadFlag]);

    const submitResponse = React.useCallback(
        (notificationId: string, response: string) => {
            setSubmittingResponse(true);
            inboxClient
                .saveResponse(notificationId, response)
                .then(() => {
                    reloadList();
                })
                .catch(handleErrorSnackbar)
                .finally(() => {
                    setSubmittingResponse(false);
                    setReloadFlag({});
                    setNotification(undefined);
                });
        },
        [inboxClient, reloadList],
    );

    const menuItems = notification && options && options.menuItems && options.menuItems({ notification: notification });
    return (
        <Slideout open={!!notificationId} onClose={() => setSelectedNotification(undefined)} menuItems={menuItems} width={480}>
            {notificationId && (
                <SlideoutContent title={notification?.subject} loading={!notification || submittingResponse}>
                    {notification && (
                        <>
                            <SlideoutContentBlock title={t('inboxDetails.sender')} value={notification.sender?.displayName} />
                            <SlideoutContentBlock
                                title={t('inboxDetails.receivedDateTime')}
                                value={t('dateTime', { value: parseISO(notification.received) })}
                            />
                            <SlideoutContentBlock title={t('inboxDetails.messageType')} value={notification.labels?.join(', ')} />
                            <SlideoutContentBlock title={t('inboxDetails.subject')} value={notification.subject} />
                            <SlideoutContentBlock title={t('inboxDetails.messageBody')}>
                                <Box
                                    sx={{
                                        p: 2,
                                        mt: 1,
                                        border: '1px solid',
                                        borderRadius: (theme) => theme.spacing(1),
                                        borderColor: 'grey.400',
                                        minHeight: 100,
                                        maxHeight: 300,
                                        overflow: 'auto',
                                    }}
                                >
                                    <Typography dangerouslySetInnerHTML={{ __html: notification.htmlMessage }} />
                                </Box>
                            </SlideoutContentBlock>
                            <SlideoutSection>
                                <SlideoutContentBlock title={t('inboxDetails.response')}>
                                    <Response notificationItem={notification} onSubmitResponse={submitResponse} />
                                </SlideoutContentBlock>
                            </SlideoutSection>
                            {!!notification.attachments?.length && (
                                <SlideoutSection>
                                    <Typography variant='h6'>{t('inboxDetails.attachments')}</Typography>
                                    <List>
                                        {notification.attachments.map((attachment) => (
                                            <ListItem key={attachment.attachmentKey} sx={{ backgroundColor: '#F5F5F5', mt: 1 }}>
                                                <Tooltip title={`${attachment.name} (${humanFileSize(attachment.size)})`} placement='top'>
                                                    <Link
                                                        component='button'
                                                        variant='body1'
                                                        noWrap
                                                        onClick={() =>
                                                            inboxClient
                                                                .createAttachmentSignature(notification.id, attachment.attachmentKey)
                                                                .then((response) =>
                                                                    window.location.assign(
                                                                        inboxClient.getDownloadAttachmentUrl(
                                                                            notification.id,
                                                                            attachment.attachmentKey,
                                                                            response.signature,
                                                                        ),
                                                                    ),
                                                                )
                                                                .catch(handleErrorSnackbar)
                                                        }
                                                    >{`${attachment.name} (${humanFileSize(attachment.size)})`}</Link>
                                                </Tooltip>
                                            </ListItem>
                                        ))}
                                    </List>
                                </SlideoutSection>
                            )}
                            {notification && options?.additionalDetails ? (
                                <SlideoutSection>{options.additionalDetails({ notification })}</SlideoutSection>
                            ) : null}
                        </>
                    )}
                </SlideoutContent>
            )}
        </Slideout>
    );
};

const SlideoutSection: React.FC<React.PropsWithChildren> = (props) => {
    return (
        <Box
            sx={{
                pt: 2,
                pb: 2,
                borderBottom: '1px solid',
                borderColor: 'rgba(0, 0, 0, 0.12)',
                '&:first-child': {
                    pt: 0,
                },
                '&:last-child': {
                    pb: 0,
                    borderBottomStyle: 'none',
                },
            }}
        >
            {props.children}
        </Box>
    );
};

const SlideoutContentBlock: React.FC<
    React.PropsWithChildren<{
        title: string;
        value?: string;
    }>
> = (props) => {
    return (
        <>
            {(props.value || props.children) && (
                <Box
                    sx={{
                        pt: 1,
                        '&:first-child': {
                            pt: 0,
                        },
                    }}
                >
                    <Typography variant='h6'>{props.title}</Typography>
                    {props.value && (
                        <Box sx={{ pl: 2, pr: 2 }}>
                            <Typography sx={{ wordBreak: 'break-word' }}>{props.value}</Typography>
                        </Box>
                    )}
                    {props.children}
                </Box>
            )}
        </>
    );
};
